import { CrudPage } from "@/components/CrudPage";
import { useFetch } from "@/hooks/useFetch";
import { StatusBadge } from "@/components/StatusBadge";
import { ClientMaterialsButton } from "@/components/ClientMaterialsButton";
import { rupiah, tanggal } from "@/lib/format";

export function ClientsPage() {
  return (
    <CrudPage title="Klien" subtitle="Master data klien" endpoint="/clients" testId="clients"
      columns={[
        { key: "code", label: "Kode" },
        { key: "name", label: "Nama Perusahaan" },
        { key: "pic", label: "PIC" },
        { key: "phone", label: "Telepon" },
        { key: "payment_term", label: "Termin", render: (r) => `${r.payment_term || 0} hari` },
        { key: "billing_flow", label: "Alur Tagihan", render: (r) => <span className="text-xs">{r.billing_flow}</span> },
        { key: "act", label: "Material", sortable: false, render: (r) => <ClientMaterialsButton client={r} /> },
      ]}
      fields={[
        { name: "code", label: "Kode Klien", required: true },
        { name: "name", label: "Nama Perusahaan", required: true },
        { name: "npwp", label: "NPWP" },
        { name: "billing_address", label: "Alamat Penagihan", type: "textarea" },
        { name: "pic", label: "PIC" },
        { name: "email", label: "Email" },
        { name: "phone", label: "Telepon" },
        { name: "payment_term", label: "Termin Pembayaran (hari)", type: "number" },
        { name: "billing_flow", label: "Alur Tagihan", type: "select", options: [
          { value: "PROFORMA_REQUIRED", label: "Proforma Wajib" },
          { value: "PROFORMA_OPTIONAL", label: "Proforma Opsional" },
          { value: "DIRECT_INVOICE", label: "Invoice Langsung" }] },
      ]}
      defaultValues={{ payment_term: 30, billing_flow: "PROFORMA_OPTIONAL", active: true }} />
  );
}

export function ProjectsPage() {
  const { data: clients } = useFetch("/clients");
  return (
    <CrudPage title="Proyek" subtitle="Master data proyek" endpoint="/projects" testId="projects"
      refs={{ clients: clients || [] }}
      columns={[
        { key: "code", label: "Kode" },
        { key: "name", label: "Nama Proyek" },
        { key: "location", label: "Lokasi" },
        { key: "start_date", label: "Mulai", render: (r) => tanggal(r.start_date) },
        { key: "rental_status", label: "Status", render: (r) => <StatusBadge status={r.rental_status} /> },
      ]}
      fields={[
        { name: "code", label: "Kode Proyek", required: true },
        { name: "client_id", label: "Klien", type: "select", optionsRef: "clients", required: true },
        { name: "name", label: "Nama Proyek", required: true },
        { name: "location", label: "Lokasi" },
        { name: "pic", label: "PIC Proyek" },
        { name: "start_date", label: "Tanggal Mulai", type: "date" },
        { name: "end_estimate", label: "Estimasi Selesai", type: "date" },
      ]}
      defaultValues={{ rental_status: "Aktif" }} />
  );
}

export function MaterialsPage() {
  return (
    <CrudPage title="Material" subtitle="Master material (tanpa harga — harga ada di tarif kontrak)" endpoint="/materials" testId="materials"
      columns={[
        { key: "code", label: "Kode" },
        { key: "name", label: "Nama Material" },
        { key: "unit", label: "Satuan" },
      ]}
      fields={[
        { name: "code", label: "Kode Material", required: true },
        { name: "name", label: "Nama Material", required: true },
        { name: "unit", label: "Satuan", required: true },
      ]} />
  );
}

export function ContractsPage() {
  const { data: clients } = useFetch("/clients");
  const { data: projects } = useFetch("/projects");
  return (
    <CrudPage title="Kontrak" subtitle="Master kontrak rental" endpoint="/contracts" testId="contracts"
      refs={{ clients: clients || [], projects: projects || [] }}
      columns={[
        { key: "number", label: "No. Kontrak" },
        { key: "valid_from", label: "Berlaku", render: (r) => `${tanggal(r.valid_from)} – ${tanggal(r.valid_to)}` },
        { key: "rate_basis", label: "Dasar Tarif" },
        { key: "billing_flow", label: "Alur", render: (r) => <span className="text-xs">{r.billing_flow}</span> },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
      ]}
      fields={[
        { name: "number", label: "Nomor Kontrak", required: true },
        { name: "client_id", label: "Klien", type: "select", optionsRef: "clients", required: true },
        { name: "project_id", label: "Proyek", type: "select", optionsRef: "projects", required: true },
        { name: "contract_date", label: "Tanggal Kontrak", type: "date" },
        { name: "valid_from", label: "Mulai Berlaku", type: "date", required: true },
        { name: "valid_to", label: "Berakhir", type: "date" },
        { name: "tax_percent", label: "PPN (%)", type: "number" },
        { name: "payment_term", label: "Termin (hari)", type: "number" },
        { name: "min_duration", label: "Minimum Masa Sewa (hari)", type: "number" },
        { name: "rate_basis", label: "Dasar Tarif", type: "select", options: [
          { value: "daily", label: "Per Hari" }, { value: "weekly", label: "Per Minggu" }, { value: "monthly", label: "Per Bulan" }] },
        { name: "duration_rounding", label: "Pembulatan Durasi", type: "select", options: [
          { value: "ceil", label: "Ke Atas" }, { value: "floor", label: "Ke Bawah" }, { value: "round", label: "Pembulatan" }] },
        { name: "count_delivery_day", label: "Hari Pengiriman Dihitung", type: "select", options: [
          { value: "true", label: "Ya" }, { value: "false", label: "Tidak" }] },
        { name: "count_return_day", label: "Hari Pengembalian Dihitung", type: "select", options: [
          { value: "true", label: "Ya" }, { value: "false", label: "Tidak" }] },
        { name: "billing_flow", label: "Alur Tagihan", type: "select", options: [
          { value: "PROFORMA_REQUIRED", label: "Proforma Wajib" },
          { value: "PROFORMA_OPTIONAL", label: "Proforma Opsional" },
          { value: "DIRECT_INVOICE", label: "Invoice Langsung" }] },
        { name: "status", label: "Status", type: "select", options: [
          { value: "Draft", label: "Draft" }, { value: "Aktif", label: "Aktif" },
          { value: "Berakhir", label: "Berakhir" }, { value: "Dibatalkan", label: "Dibatalkan" }] },
        { name: "notes", label: "Catatan", type: "textarea" },
      ]}
      defaultValues={{ tax_percent: 11, rate_basis: "daily", duration_rounding: "ceil", count_delivery_day: "true", count_return_day: "false", status: "Draft", currency: "IDR" }} />
  );
}
